import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, Loader2, X, Lock, Calendar, MapPin, AlertCircle, CheckCircle } from "lucide-react";
import { cn, formatReportDate } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { Skeleton } from "@/components/ui/skeleton";

// G3.15: Location detection result interface
interface LocationDetectionResult {
  locationCode: string | null;
  locationLabel: string | null;
  isValidLocation: boolean;
  confidence: "high" | "medium" | "low" | "none";
  diagnostics?: {
    zonesFound: string[];
    locationVotes: Record<string, number>;
    warnings: string[];
  };
}

export interface ReportUploadDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialLocation?: string;
  initialReportDate?: string;
  lockLocationAndDate?: boolean;
  onSuccess?: (newReportId: string) => void;
}

interface UploadResponse {
  reportId: string;
  message?: string;
}

export function ReportUploadDrawer({
  open,
  onOpenChange,
  initialLocation = "",
  initialReportDate,
  lockLocationAndDate = false,
  onSuccess,
}: ReportUploadDrawerProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>(initialLocation);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();
  
  // G3.15: Location detection state for Upload New Version flow
  const [detectedLocation, setDetectedLocation] = useState<LocationDetectionResult | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState<string | null>(null);
  
  // Gate F6.6: Dynamic locations from API
  const { locations: activeLocations, isLoading: isLocationsLoading, error: locationsError } = useActiveLocations();
  
  // G3.15: Detect location when file is selected (for locked location mode)
  useEffect(() => {
    if (!selectedFile || !lockLocationAndDate) {
      setDetectedLocation(null);
      setDetectionError(null);
      return;
    }

    const detectLocation = async () => {
      setIsDetecting(true);
      setDetectionError(null);
      setDetectedLocation(null);

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        
        const result = await apiRequest("POST", "/api/reports/detect-location", formData) as LocationDetectionResult;
        
        if (!result.locationCode || !result.isValidLocation) {
          setDetectionError(
            result.diagnostics?.warnings?.[0] || 
            "Could not detect a valid location from this PDF."
          );
        } else {
          setDetectedLocation(result);
          // Check for location mismatch
          if (result.locationCode !== initialLocation) {
            setDetectionError(
              `This PDF appears to be for ${result.locationLabel || result.locationCode}, not ${initialLocation}. Please select the correct PDF file.`
            );
          }
        }
      } catch (error) {
        setDetectionError("Failed to analyze PDF. Please try again.");
      } finally {
        setIsDetecting(false);
      }
    };

    detectLocation();
  }, [selectedFile, lockLocationAndDate, initialLocation]);

  const uploadMutation = useMutation({
    mutationFn: async ({ file, location, reportDate }: { file: File; location: string; reportDate: string }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("location", location);
      formData.append("reportDate", reportDate);
      
      const response = await apiRequest("POST", "/api/reports/upload", formData);
      return response as UploadResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/reports"] });
      
      if (lockLocationAndDate) {
        toast({
          title: "New version uploaded",
          description: "You're now viewing the latest version.",
        });
      } else {
        toast({
          title: "Upload successful",
          description: "Report is being processed. This may take a few moments.",
        });
      }
      
      setSelectedFile(null);
      if (!lockLocationAndDate) {
        setSelectedLocation("");
      }
      onOpenChange(false);
      
      if (onSuccess && data.reportId) {
        onSuccess(data.reportId);
      }
    },
    onError: () => {
      toast({
        title: "Upload failed",
        description: "Please check the file and try again.",
        variant: "destructive",
      });
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    const location = lockLocationAndDate ? initialLocation : selectedLocation;
    
    let reportDate: string;
    if (lockLocationAndDate && initialReportDate) {
      reportDate = initialReportDate;
    } else if (selectedFile) {
      const dateMatch = selectedFile.name.match(/(\d{4}-\d{2}-\d{2})/);
      reportDate = dateMatch ? dateMatch[1] : new Date().toISOString().split('T')[0];
    } else {
      reportDate = new Date().toISOString().split('T')[0];
    }
    
    if (selectedFile && location) {
      uploadMutation.mutate({ file: selectedFile, location, reportDate });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedFile(null);
      setDetectedLocation(null);
      setDetectionError(null);
      if (!lockLocationAndDate) {
        setSelectedLocation("");
      }
    } else {
      setSelectedLocation(initialLocation);
    }
    onOpenChange(newOpen);
  };
  
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setDetectedLocation(null);
    setDetectionError(null);
  };

  const effectiveLocation = lockLocationAndDate ? initialLocation : selectedLocation;
  
  // G3.15: For locked location mode, require successful detection with matching location
  const locationValidForUpload = lockLocationAndDate 
    ? (detectedLocation?.isValidLocation && detectedLocation?.locationCode === initialLocation && !detectionError)
    : true;
  
  const canUpload = selectedFile && effectiveLocation && !uploadMutation.isPending && !isDetecting && locationValidForUpload && (!lockLocationAndDate ? !isLocationsLoading && !locationsError : true);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="report-upload-drawer"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {lockLocationAndDate ? "Upload New Version" : "Upload Report"}
          </SheetTitle>
          <SheetDescription>
            {lockLocationAndDate 
              ? "Upload a new PDF to replace the current version" 
              : "Upload a daily PDF report for processing"}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          {lockLocationAndDate && initialLocation && initialReportDate && (
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Location:</span>
                <Badge variant="outline" className="gap-1">
                  <Lock className="h-3 w-3" />
                  {initialLocation}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Report Date:</span>
                <Badge variant="outline" className="gap-1">
                  <Lock className="h-3 w-3" />
                  {formatReportDate(initialReportDate)}
                </Badge>
              </div>
            </div>
          )}

          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
              "hover:border-primary hover:bg-primary/5"
            )}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            data-testid="upload-dropzone"
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm font-medium mb-2">
              Drag and drop your PDF report here, or click to browse
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Only PDF files are accepted (max 10MB)
            </p>
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="drawer-file-upload"
              data-testid="input-drawer-file-upload"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById("drawer-file-upload")?.click()}
              data-testid="button-drawer-select-file"
            >
              Select File
            </Button>
          </div>

          {selectedFile && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-8 w-8 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRemoveFile}
                data-testid="button-drawer-remove-file"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* G3.15: Location detection status for Upload New Version flow */}
          {selectedFile && lockLocationAndDate && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Location Verification</label>
              {isDetecting ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg" data-testid="drawer-location-detecting">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Verifying PDF location...</span>
                </div>
              ) : detectionError ? (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg" data-testid="drawer-location-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{detectionError}</span>
                </div>
              ) : detectedLocation?.isValidLocation && detectedLocation?.locationCode === initialLocation ? (
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg" data-testid="drawer-location-verified">
                  <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                  <span className="text-sm font-medium text-success">Location verified: {detectedLocation.locationLabel}</span>
                </div>
              ) : null}
            </div>
          )}

          {!lockLocationAndDate && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Location</label>
              <Select 
                value={selectedLocation} 
                onValueChange={setSelectedLocation}
                disabled={isLocationsLoading}
              >
                <SelectTrigger data-testid="select-drawer-location">
                  {isLocationsLoading ? (
                    <Skeleton className="h-4 w-24" />
                  ) : (
                    <SelectValue placeholder="Select location" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {locationsError ? (
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      Unable to load locations
                    </div>
                  ) : (
                    activeLocations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.code} data-testid={`option-drawer-location-${loc.code.toLowerCase()}`}>
                        {loc.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={!canUpload}
            data-testid="button-drawer-upload"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              lockLocationAndDate ? "Upload New Version" : "Upload Report"
            )}
          </Button>

          {uploadMutation.isPending && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg" data-testid="drawer-upload-progress">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Uploading and processing report...
                </span>
              </div>
              <Progress value={75} className="h-2" />
              <p className="text-xs text-muted-foreground">
                This may take a few moments depending on the file size.
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
